import React, { useRef, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Modal,
  OverlayTrigger,
  Row,
  Stack,
  Tooltip,
} from 'react-bootstrap';
import { useForm } from 'react-hook-form';

import _ from 'lodash';
import moment from 'moment';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import CustomModal from 'src/components/custom_components/CustomModal/CustomModal';

import { ApplicationInstance } from 'src/models/Application.model';
import {
  isCvdTemplateWithInvalidAge,
  MAX_CVD_AGE,
  MIN_CVD_AGE,
} from 'src/models/CvdLevel.model';
import { DilationEnum } from 'src/models/Dilation.model';
import { Gender } from 'src/models/Gender.model';
import {
  ReportTemplate,
  ReportTemplateName,
} from 'src/models/ReportTemplate.model';
import { Session } from 'src/models/Session.model';

import useNavigateAfterConfirm from 'src/hooks/useNavigateAfterConfirm';
import useSubmitSession from 'src/hooks/useSubmitSession';
import useUiTranslation from 'src/hooks/useUiTranslation';

export type AIPatientDataTypes = {
  isLeftEyeDilated: DilationEnum;
  isRightEyeDilated: DilationEnum;
  template: ReportTemplate;
  gender: Gender;
};

type AIPatientConfirmationModalProps = {
  formValues: Partial<Session>;
  isShowConfirmModal: boolean;
  handleCloseConfirmModal: () => void;
  InfoConfirmationKeyMap: { [key in keyof Partial<Session>]?: string };
  resetPatientForm: (data: Partial<Session>) => void;
};

export default function AIPatientConfirmationModal({
  formValues,
  isShowConfirmModal,
  handleCloseConfirmModal,
  InfoConfirmationKeyMap,
  resetPatientForm,
}: AIPatientConfirmationModalProps) {
  const {
    practiceSettings: { product, camera },
    updateSession,
  } = useRemoteBoundStore();

  const { initializeRetinalExam } = useSubmitSession();
  const navigateAfterConfirm = useNavigateAfterConfirm();

  const { tCommon } = useUiTranslation();

  const formRef = useRef<HTMLFormElement>(null);
  const [showGenderWarning, setShowGenderWarning] = useState(false);
  const {
    handleSubmit,
    register,
    watch,
    reset,
    formState: { isSubmitting },
  } = useForm({
    defaultValues: {
      isLeftEyeDilated: DilationEnum.NotDilated,
      isRightEyeDilated: DilationEnum.NotDilated,
      template: ApplicationInstance(product, camera).templates[0],
      gender: Gender.UNSPECIFIED,
    },
    mode: 'onChange',
  });

  const handleCloseAndResetConfirmModal = () => {
    handleCloseConfirmModal();
    reset();
    resetPatientForm({
      dob: formValues.dob,
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      id: formValues.id,
    });
  };

  const isContainCVDAlgorithm = watch('template')?.includes('cvd');

  const handleConfirm = () => {
    if (isContainCVDAlgorithm && !watch('gender')) {
      setShowGenderWarning(true);
      return;
    }
    setShowGenderWarning(false);
    handleSubmit(async (data: AIPatientDataTypes) => {
      const tempSession = {
        id: formValues.id,
        orderId: formValues.orderId,
        patientUserId: formValues.patientUserId,
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        dob: formValues.dob,
        age: formValues.age,
        gender: data.gender,
        leftEyeDilatedTime:
          data.isLeftEyeDilated === DilationEnum.Dilated
            ? new Date()
            : undefined,
        rightEyeDilatedTime:
          data.isRightEyeDilated === DilationEnum.Dilated
            ? new Date()
            : undefined,
        template: data.template,
      };
      updateSession(tempSession);

      await initializeRetinalExam(tempSession);
      navigateAfterConfirm();
    })();
  };

  const age = moment(new Date()).diff(formValues.dob, 'years');
  return (
    <CustomModal
      show={isShowConfirmModal}
      onHide={handleCloseAndResetConfirmModal}
      scrollable={true}
      fullscreen="xl-down"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {tCommon('patientFields.confirmPatientInformation')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form ref={formRef}>
          <Card className="mb-4">
            <Card.Body>
              <Stack gap={2}>
                {(
                  ['dob', 'firstName', 'lastName', 'id'] as Array<
                    keyof Omit<
                      Session,
                      'leftEyeDilatedTime' | 'rightEyeDilatedTime' | 'template'
                    >
                  >
                ).map((objKey) => (
                  <Row key={objKey}>
                    <Col>
                      <span className="fw-medium">
                        {tCommon(
                          `patientFields.${InfoConfirmationKeyMap[objKey]}`,
                        )}
                      </span>
                    </Col>
                    <Col>
                      <span>{formValues[objKey]?.toString()}</span>
                    </Col>
                  </Row>
                ))}
              </Stack>
            </Card.Body>
          </Card>
          <div className="border rounded p-3 shadow">
            <Stack gap={2}>
              <Form.Group as={Row} className="mb-3">
                <Form.Label column className="fw-medium">
                  {tCommon('examFields.leftEyeDilation')}
                </Form.Label>

                <Col>
                  <Form.Group className="gap-2">
                    <Form.Check
                      type="radio"
                      label={tCommon('examFields.notDilated')}
                      value={DilationEnum.NotDilated}
                      {...register('isLeftEyeDilated')}
                    />
                    <Form.Check
                      type="radio"
                      label={tCommon('examFields.dilated')}
                      value={DilationEnum.Dilated}
                      {...register('isLeftEyeDilated')}
                    />
                  </Form.Group>
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-3">
                <Form.Label column className="fw-medium">
                  {tCommon('examFields.rightEyeDilation')}
                </Form.Label>
                <Col>
                  <Form.Group className="gap-2">
                    <Form.Check
                      type="radio"
                      label={tCommon('examFields.notDilated')}
                      value="Not Dilated"
                      {...register('isRightEyeDilated')}
                    />
                    <Form.Check
                      type="radio"
                      label={tCommon('examFields.dilated')}
                      value="Dilated"
                      {...register('isRightEyeDilated')}
                    />
                  </Form.Group>
                </Col>
              </Form.Group>

              <Form.Group as={Row}>
                <Col>
                  <Form.Label className="fw-medium">
                    {tCommon('examFields.selectReportType')}
                  </Form.Label>
                </Col>
                <Col>
                  {ApplicationInstance(product, camera).templates.map(
                    (template) =>
                      isCvdTemplateWithInvalidAge(template, age) ? (
                        <OverlayTrigger
                          placement="bottom"
                          overlay={
                            <Tooltip>
                              {tCommon('examFields.cardiovascularAnalysis', {
                                minAge: MIN_CVD_AGE,
                                maxAge: MAX_CVD_AGE,
                              })}
                            </Tooltip>
                          }
                          key={template}
                        >
                          <span className="d-inline-block">
                            <Form.Check
                              key={template}
                              type="radio"
                              label={tCommon(
                                `report.reportTemplateFields.${ReportTemplateName(template)}`,
                              )}
                              value={template}
                              disabled={true}
                              {...register('template')}
                            />
                          </span>
                        </OverlayTrigger>
                      ) : (
                        <Form.Check
                          key={template}
                          type="radio"
                          label={tCommon(
                            `report.reportTemplateFields.${ReportTemplateName(template)}`,
                          )}
                          value={template}
                          {...register('template')}
                        />
                      ),
                  )}
                </Col>
              </Form.Group>

              {isContainCVDAlgorithm && (
                <Form.Group as={Row}>
                  <Col>
                    <Form.Label className="fw-medium">
                      {tCommon(`patientFields.sex`)}
                    </Form.Label>
                  </Col>
                  <Col>
                    <Stack>
                      {Object.values(Gender).map((gender) => (
                        <Form.Check
                          key={gender}
                          type="radio"
                          label={_.capitalize(
                            tCommon(`patientFields.${gender}`),
                          )}
                          value={gender}
                          {...register('gender', {
                            onChange: () => setShowGenderWarning(false),
                          })}
                        />
                      ))}
                    </Stack>
                    {showGenderWarning && (
                      <Form.Text className="text-danger">
                        {tCommon('examFields.patientSexRequired')}
                      </Form.Text>
                    )}
                  </Col>
                </Form.Group>
              )}
            </Stack>
          </div>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="primary-100"
          onClick={handleCloseAndResetConfirmModal}
          disabled={isSubmitting}
        >
          {tCommon('modal.cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={isSubmitting}
        >
          {isSubmitting
            ? tCommon('modal.confirming')
            : tCommon('modal.confirm')}
        </Button>
      </Modal.Footer>
    </CustomModal>
  );
}
